import { api } from './api';

/* Mirrors Eduvora.Application.QuestionBank source material. */

/** ⚠ Spelled exactly as the server's enum. A value it cannot parse is a 400 the teacher reads as
    "the upload failed" after waiting for a 40-page extraction. */
export type SourceKind = 'Ncert' | 'SchoolMaterial' | 'Textbook' | 'Other';

export const SOURCE_KINDS: readonly { name: SourceKind; label: string; hint: string }[] = [
  { name: 'Ncert', label: 'NCERT', hint: 'the prescribed book' },
  { name: 'SchoolMaterial', label: "The school's own notes", hint: 'a handout, a worksheet, typed notes' },
  { name: 'Textbook', label: 'Another textbook', hint: 'a reference the school buys' },
  { name: 'Other', label: 'Something else', hint: '' },
];

export function kindLabel(kind: string): string {
  return SOURCE_KINDS.find((k) => k.name === kind)?.label ?? kind;
}

/** One page, with the page number the FILE gave it — see `extractPdfPages`. */
export interface SourcePage { page: number; text: string }

export interface SourceSummary {
  id: string;
  title: string;
  attribution: string | null;
  kind: string;
  subjectName: string | null;
  classLabel: string | null;
  unitName: string | null;
  pageCount: number;
  characterCount: number;
  /** True when this sits in the platform-wide library (NCERT), not the school's own. Shared
      material is readable by every school and removable by none of them. */
  shared: boolean;
  createdAt: string;
}

export interface SourceBody {
  institutionId?: string | null;
  title: string;
  attribution?: string | null;
  kind: SourceKind;
  subjectId?: string | null;
  classLabel?: string | null;
  unitId?: string | null;
  pages: SourcePage[];
}

export const sourcesApi = {
  list: (institutionId?: string | null, subjectId?: string | null) =>
    api.get<SourceSummary[]>(`/api/qbank/sources?${new URLSearchParams({
      ...(institutionId ? { institutionId } : {}),
      ...(subjectId ? { subjectId } : {}),
    })}`),

  /** `chunks` is how many pieces the server split the text into for retrieval — the number that
      decides whether the assistant can actually find anything in this chapter. */
  create: (b: SourceBody) => api.post<{ id: string; chunks: number }>('/api/qbank/sources', b),

  /** Retires rather than deletes: questions already drafted from this chapter keep their citation. */
  retire: (id: string) => api.del<{ retired: true }>(`/api/qbank/sources/${id}`),

  /** ⚠ Inclusive range, and deliberately not "give me the whole book" — a 300-page textbook is
      megabytes of JSON and the reader only ever shows a handful of pages at a time. */
  pages: (id: string, from: number, to: number) =>
    api.get<{ pages: SourcePage[] }>(`/api/qbank/sources/${id}/pages?${new URLSearchParams({
      from: String(from), to: String(to),
    })}`),
};

// ---------------------------------------------------------------------- extraction

/**
 * ⚠ THE PDF IS READ IN THE BROWSER. THE SERVER NEVER SEES THE FILE. Three reasons, all deliberate:
 *
 *  1. It keeps a PDF parser out of the API entirely. A PDF parser is a large, historically
 *     exploitable surface being fed arbitrary uploads by anybody with a teacher login; here the
 *     worst a malformed file can do is break one tab.
 *  2. A scanned chapter is 30 MB and the text in it is a few hundred KB. Sending the text is the
 *     difference between an upload that finishes on a school's connection and one that does not.
 *  3. The page numbers are the file's own. Every question drafted from this chapter cites a page
 *     and somebody will open the book to check it, so the number has to be real rather than a
 *     character count divided by an assumed page length.
 *
 * ⚠ pdfjs-dist is loaded with a dynamic import, and that is load-bearing rather than tidy: the
 * library is about a megabyte. A static import puts it in the main bundle and every parent
 * checking a fee balance on a 3G phone downloads a PDF parser they will never run. This way it is
 * its own chunk, fetched the first time somebody picks a file.
 */
export async function extractPdfPages(
  file: File,
  onProgress: (done: number, total: number) => void,
  isCancelled: () => boolean = () => false,
): Promise<SourcePage[]> {
  const [pdfjs, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    // ⚠ The worker is a separate file on purpose. Parsing runs off the main thread, which is the
    // only reason the progress bar below can actually move — on the main thread the browser would
    // paint 0% and then 100%, which is precisely the frozen screen this is meant to avoid.
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl.default;

  // ⚠ pdf.js takes ownership of this buffer and detaches it. Never hand it the same ArrayBuffer
  // twice — read it from the File again instead.
  const data = new Uint8Array(await file.arrayBuffer());

  // verbosity 0 = errors only. The default narrates every unsupported annotation to the console,
  // which buries the one message that matters when a teacher sends us a screenshot.
  const task = pdfjs.getDocument({ data, verbosity: 0 });
  try {
    const doc = await task.promise;
    const total = doc.numPages;
    onProgress(0, total);

    const pages: SourcePage[] = [];
    for (let n = 1; n <= total; n++) {
      if (isCancelled()) break;
      const page = await doc.getPage(n);
      const content = await page.getTextContent();
      pages.push({ page: n, text: joinRuns(content.items) });
      // Without this a long chapter holds every page's operator list in memory at once.
      page.cleanup();
      onProgress(n, total);
    }
    return pages;
  } finally {
    await task.destroy();
  }
}

/* Structural, rather than pdfjs-dist's own TextItem/TextMarkedContent: those are not re-exported
   from the package root, and reaching into `pdfjs-dist/types/src/...` ties us to its file layout. */
type TextRun = { str: string; hasEOL: boolean };
type ContentMarker = { type: string };

/**
 * A page of pdf.js text is a list of positioned runs, not a paragraph.
 *
 * ⚠ The list also contains marked-content markers, which have no `str` at all. Joining blind
 * scatters "undefined" through the chapter — and it survives into the questions drafted from it.
 */
function joinRuns(items: readonly (TextRun | ContentMarker)[]): string {
  let out = '';
  for (const item of items) {
    if (!('str' in item)) continue;
    out += item.str;
    if (item.hasEOL) out += '\n';
  }
  return out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * ⚠ Below this many characters a page has, for our purposes, nothing on it. It is not zero because
 * a scanned page is rarely empty — pdf.js still finds the running header the scanner's OCR-less
 * export left behind ("104  PHYSICS"), and a chapter of those would otherwise look like text.
 */
const EMPTY_PAGE_CHARS = 40;

export interface TextVerdict {
  characters: number;
  pages: number;
  emptyPages: number;
  /** Nothing usable at all — saving this would store a document with no content in it. */
  blank: boolean;
  /** Enough pages came back empty that this is almost certainly a photocopy. */
  looksScanned: boolean;
}

export function judgeText(pages: SourcePage[]): TextVerdict {
  const characters = pages.reduce((n, p) => n + p.text.trim().length, 0);
  const emptyPages = pages.filter((p) => p.text.trim().length < EMPTY_PAGE_CHARS).length;
  return {
    characters,
    pages: pages.length,
    emptyPages,
    blank: characters < EMPTY_PAGE_CHARS,
    // Half a chapter missing is not a bad page, it is a scan.
    looksScanned: pages.length > 0 && emptyPages / pages.length >= 0.5,
  };
}

/** The sentence itself, because it is the one thing on this screen that has to be right. */
export const SCAN_WARNING =
  'This looks like a scan. Eduvora can only read PDFs that contain real text; a photographed or ' +
  'scanned chapter will need to be typed or run through OCR first.';

/**
 * The typed-notes path.
 *
 * ⚠ Page numbers still matter here — a citation that says "page 3 of Ms Rao's notes" is checkable
 * and "somewhere in Ms Rao's notes" is not. A line of three or more dashes splits a page, and so
 * does a form feed, which is what a PDF reader puts in when a teacher copies out of one.
 */
export function pagesFromTypedText(text: string, firstPage = 1): SourcePage[] {
  const blocks = text.split(/\f|^[ \t]*-{3,}[ \t]*$/m);
  return blocks
    .map((b) => b.trim())
    .filter((b) => b.length > 0)
    .map((b, i) => ({ page: firstPage + i, text: b }));
}
